<template>
    <section class="main">

        <div v-if="isLoading" class="loader-container">
            <loader size="50px" />
        </div>

        <template v-else>
            <div class="reimbursement-info">
                <p>With usage of automatic reimbursement you are getting reimbursement every day, as far as someone is
                    using your account securely via appdb. This helps you to get full reimbursement of your account in
                    the end of the year and extend it to further years. While you keep active devices even after a year
                    -
                    reimbursement continues even further to next years!</p>
            </div>

            <!-- Main view for /my/auto-reimbursement/ -->
            <div v-if="!isDevicesView && !isWithdrawalsView">
                <!-- Always show contract text, even if device is not linked -->
                <div v-if="contract && (!hasSignedAgreement || !isDeviceLinked)" class="section">
                    <v-markdown :content="contract" />
                </div>

                <!-- Display device linking prompt if no device is linked -->
                <div v-if="!isDeviceLinked" class="section device-linking">
                    <h2 class="form-title">To use Auto Reimbursement, you need to link your device first</h2>
                    <router-link to="/link" class="btn-primary">Link Your Device</router-link>
                </div>

                <!-- Show agreement form or details only if device is linked -->
                <div v-else-if="!hasSignedAgreement" class="section form-section">
                    <h2 class="form-title">To enable automatic reimbursement please fill in this form and click
                        "continue".
                        Submitting of this form creates legal binding between you and appdb.</h2>

                    <form @submit.prevent="submitForm">
                        <div class="form-group">
                            <label for="name">Name, Surname</label>
                            <input type="text" id="name" v-model="formData.name" required class="form-control">
                        </div>

                        <div class="form-group">
                            <label for="address">Address</label>
                            <input type="text" id="address" v-model="formData.address" required class="form-control">
                        </div>

                        <div class="form-row">
                            <div class="form-group">
                                <label for="city">City</label>
                                <input type="text" id="city" v-model="formData.city" required class="form-control">
                            </div>

                            <div class="form-group">
                                <label for="state">State/Province</label>
                                <input type="text" id="state" v-model="formData.state" class="form-control">
                            </div>
                        </div>

                        <div class="form-row">
                            <div class="form-group">
                                <label for="index">Postal/Zip Code</label>
                                <input type="text" id="index" v-model="formData.index" required class="form-control">
                            </div>

                            <div class="form-group">
                                <label for="country">Country</label>
                                <select id="country" v-model="formData.country" required class="form-control">
                                    <option value="" disabled>Select a country</option>
                                    <option v-for="country in countries" :key="country" :value="country">{{ country }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <div class="form-group">
                            <label for="bank_details">Payout Details</label>
                            <p class="bank-info">We support payouts via PayPal, wise.com or SWIFT bank transfer. Account
                                should be opened in your name, we can not transfer funds to third party accounts.</p>
                            <textarea id="bank_details" v-model="formData.bank_details" required class="form-control"
                                rows="4"></textarea>
                        </div>

                        <div class="form-actions">
                            <button type="submit" class="btn-primary">Continue</button>
                        </div>
                    </form>
                </div>

                <!-- Show usage section, then agreement details -->
                <div v-else-if="isDeviceLinked && hasSignedAgreement" class="section agreement-details">
                    <div class="usage-section">
                        <h3 class="section-subtitle">Usage</h3>

                        <div class="usage-grid">
                            <div class="usage-card">
                                <div class="usage-header">
                                    <h4 class="usage-title">Devices</h4>
                                    <router-link to="/my/auto-reimbursement/devices" class="details-link">
                                        View details
                                    </router-link>
                                </div>
                                <div class="usage-value">{{ deviceCount }}</div>
                                <div class="usage-label">Registered devices, {{ availableSlots }} available</div>
                            </div>

                            <div class="usage-card">
                                <h4 class="usage-title">Balance</h4>
                                <div class="usage-value">€{{ financialDetails.balance }}</div>
                                <div class="usage-label">Current balance</div>
                            </div>

                            <div class="usage-card">
                                <h4 class="usage-title">Available</h4>
                                <div class="usage-value">€{{ financialDetails.availableForWithdrawal }}</div>
                                <div class="usage-label">Available for withdrawal</div>
                                <button v-if="parseFloat(financialDetails.availableForWithdrawal) > 0"
                                    class="btn-withdrawal" :disabled="isWithdrawalProcessing"
                                    @click="requestWithdrawal">
                                    {{ isWithdrawalProcessing ? 'Processing...' : 'Request withdrawal' }}
                                </button>
                            </div>

                            <div class="usage-card">
                                <div class="usage-header">
                                    <h4 class="usage-title">Pending</h4>
                                    <router-link to="/my/auto-reimbursement/withdrawals" class="details-link">
                                        View details
                                    </router-link>
                                </div>
                                <div class="usage-value">€{{ financialDetails.withdrawalPending }}</div>
                                <div class="usage-label">Pending withdrawal</div>
                            </div>

                            <div class="usage-card">
                                <div class="usage-header">
                                    <h4 class="usage-title">Completed</h4>
                                    <router-link to="/my/auto-reimbursement/withdrawals" class="details-link">
                                        View details
                                    </router-link>
                                </div>
                                <div class="usage-value">€{{ financialDetails.withdrawalCompleted }}</div>
                                <div class="usage-label">Completed withdrawal</div>
                            </div>
                        </div>
                    </div>

                    <div class="agreement-section">
                        <h2 class="form-title">Automatic Reimbursement Agreement</h2>
                        <p>You have signed an automatic reimbursement agreement with the following details:</p>

                        <div class="details-container">
                            <div class="detail-row">
                                <span class="detail-label">Name:</span>
                                <span class="detail-value">{{ agreementDetails.name }}</span>
                            </div>
                            <div class="detail-row">
                                <span class="detail-label">Address:</span>
                                <span class="detail-value">{{ agreementDetails.address }}</span>
                            </div>
                            <div class="detail-row">
                                <span class="detail-label">City:</span>
                                <span class="detail-value">{{ agreementDetails.city }}</span>
                            </div>
                            <div class="detail-row">
                                <span class="detail-label">State/Province:</span>
                                <span class="detail-value">{{ agreementDetails.state }}</span>
                            </div>
                            <div class="detail-row">
                                <span class="detail-label">Postal/Zip Code:</span>
                                <span class="detail-value">{{ agreementDetails.index }}</span>
                            </div>
                            <div class="detail-row">
                                <span class="detail-label">Country:</span>
                                <span class="detail-value">{{ agreementDetails.country }}</span>
                            </div>
                            <div class="detail-row">
                                <span class="detail-label">Payout Details:</span>
                                <span class="detail-value">{{ agreementDetails.bank_details }}</span>
                            </div>
                            <div class="detail-row">
                                <span class="detail-label">Signed At:</span>
                                <span class="detail-value">{{ formattedSignedDate }}</span>
                            </div>
                        </div>

                        <br />
                        <p>To withdraw from this agreement or change your information, please create ticket in <a
                                href="https://aysa-support.freshdesk.com">support desk</a>.
                        </p>
                    </div>
                </div>
            </div>

            <!-- Devices Details View for /my/auto-reimbursement/devices -->
            <div v-else-if="isDevicesView && isDeviceLinked && hasSignedAgreement" class="devices-details">
                <div class="back-section">
                    <router-link to="/my/auto-reimbursement" class="back-link">
                        &larr; Back to Auto Reimbursement
                    </router-link>
                </div>

                <h2 class="section-title">Registered Devices</h2>

                <div v-if="loadingDevices" class="loader-container">
                    <loader size="30px" />
                </div>
                <div v-else>
                    <div v-if="devices.length === 0" class="no-items-notice">
                        No items to show
                    </div>
                    <div v-else-if="!isMobile" class="table-devices-wrapper">
                        <table class="table-devices">
                            <thead class="table-devices__head table-devices__row">
                                <tr>
                                    <th class="table-devices__cell">UDID</th>
                                    <th class="table-devices__cell">{{ $t('PURCHASE_DATE') }}</th>
                                    <th class="table-devices__cell">Current end date</th>
                                    <th class="table-devices__cell">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr class="table-devices__row" v-for="device in devices" :key="device.udid">
                                    <td class="table-devices__cell">{{ device.udid }}</td>
                                    <td class="table-devices__cell">
                                        {{ new Date(device.added_at * 1000).toLocaleDateString() }}
                                    </td>
                                    <td class="table-devices__cell">
                                        {{ new Date(device.expires_at * 1000).toLocaleDateString() }}
                                    </td>
                                    <td class="table-devices__cell">
                                        <span class="status-indicator"
                                            :class="device.expires_at * 1000 > Date.now() ? 'status-indicator--negative' : 'status-indicator--positive'">
                                            {{ device.expires_at * 1000 > Date.now() ? 'Active, removal not allowed' :
                                                'Inactive, removal allowed' }}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <important-message v-if="deviceResetAt" type="notice" class="device-reset-message">
                            You can log in to <a target="_blank"
                                href="https://developer.apple.com/account/resources/devices/list">Device list</a> and
                            remove inactive devices after {{ formatDeviceResetDate }}. If there are active devices with
                            end of your membership, <strong>you need to renew it with Apple</strong> in order to
                            continue to receive automatic reimbursement.
                        </important-message>
                    </div>
                    <div v-else class="devices">
                        <div class="device" v-for="device in devices" :key="device.udid">
                            <span class="device__heading">UDID</span>
                            <span>{{ device.udid }}</span>
                            <span class="device__heading">{{ $t('PURCHASE_DATE') }}</span>
                            <span>{{ new Date(device.added_at * 1000).toLocaleDateString() }}</span>
                            <span class="device__heading">Current end date</span>
                            <span>{{ new Date(device.expires_at * 1000).toLocaleDateString() }}</span>
                            <span class="device__heading">Status</span>
                            <span>
                                <span class="status-indicator"
                                    :class="device.expires_at * 1000 > Date.now() ? 'status-indicator--negative' : 'status-indicator--positive'">
                                    {{ device.expires_at * 1000 > Date.now() ? 'Active, removal not allowed' :
                                        'Inactive, removal allowed' }}
                                </span>
                            </span>
                        </div>
                        <important-message v-if="deviceResetAt" type="notice" class="device-reset-message">
                            You can log in to <a target="_blank"
                                href="https://developer.apple.com/account/resources/devices/list">Device list</a> and
                            remove inactive devices after {{ formatDeviceResetDate }}. If there are active devices with
                            end of your membership, <strong>you need to renew it with Apple</strong> in order to
                            continue to receive automatic reimbursement.
                        </important-message>
                    </div>
                </div>
            </div>

            <!-- Add the Withdrawals Details View -->
            <div v-else-if="isWithdrawalsView && isDeviceLinked && hasSignedAgreement" class="withdrawals-details">
                <div class="back-section">
                    <router-link to="/my/auto-reimbursement" class="back-link">
                        &larr; Back to Auto Reimbursement
                    </router-link>
                </div>

                <h2 class="section-title">Withdrawal History</h2>

                <div v-if="loadingWithdrawals" class="loader-container">
                    <loader size="30px" />
                </div>
                <div v-else>
                    <div v-if="withdrawals.length === 0" class="no-items-notice">
                        No items to show
                    </div>
                    <div v-else-if="!isMobile" class="table-withdrawals-wrapper">
                        <table class="table-withdrawals">
                            <thead class="table-withdrawals__head table-withdrawals__row">
                                <tr>
                                    <th class="table-withdrawals__cell">ID</th>
                                    <th class="table-withdrawals__cell">Amount</th>
                                    <th class="table-withdrawals__cell">Status</th>
                                    <th class="table-withdrawals__cell">Date</th>
                                    <th class="table-withdrawals__cell">Invoice</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr class="table-withdrawals__row" v-for="withdrawal in withdrawals"
                                    :key="withdrawal.id">
                                    <td class="table-withdrawals__cell">#{{ withdrawal.id }}</td>
                                    <td class="table-withdrawals__cell">€{{ formatCurrency(withdrawal.amount_eur) }}
                                    </td>
                                    <td class="table-withdrawals__cell">
                                        <div>{{ withdrawal.status_translated }}</div>
                                        <div v-if="withdrawal.status_text" class="table-withdrawals__status-text">
                                            {{ withdrawal.status_text }}
                                        </div>
                                    </td>
                                    <td class="table-withdrawals__cell">
                                        {{ new Date(withdrawal.added_at * 1000).toLocaleDateString() }}
                                    </td>
                                    <td class="table-withdrawals__cell">
                                        <a v-if="withdrawal.invoice_uri" :href="withdrawal.invoice_uri" target="_blank"
                                            class="invoice-link">Download</a>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div v-else class="withdrawals">
                        <div class="withdrawal" v-for="withdrawal in withdrawals" :key="withdrawal.id">
                            <span class="withdrawal__heading">ID</span>
                            <span>#{{ withdrawal.id }}</span>
                            <span class="withdrawal__heading">Amount</span>
                            <span>€{{ formatCurrency(withdrawal.amount_eur) }}</span>
                            <span class="withdrawal__heading">Status</span>
                            <span>
                                <div>{{ withdrawal.status_translated }}</div>
                                <div v-if="withdrawal.status_text" class="withdrawal__status-text">
                                    {{ withdrawal.status_text }}
                                </div>
                            </span>
                            <span class="withdrawal__heading">Date</span>
                            <span>{{ new Date(withdrawal.added_at * 1000).toLocaleDateString() }}</span>
                            <span class="withdrawal__heading">Invoice</span>
                            <span>
                                <a v-if="withdrawal.invoice_uri" :href="withdrawal.invoice_uri" target="_blank"
                                    class="invoice-link">Download</a>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Show device linking prompt if trying to access protected views without device -->
            <div v-else-if="(isDevicesView || isWithdrawalsView) && !isDeviceLinked" class="section device-linking">
                <h2 class="form-title">To view this page, you need to link your device first</h2>
                <router-link to="/link" class="btn-primary">Link Your Device</router-link>
            </div>
        </template>
    </section>
</template>

<script>
import Vue from 'vue';
import Loader from '@/components/Loader';
import VMarkdown from '@/components/VMarkdown.vue';
import ImportantMessage from '@/components/ImportantMessage.vue';

export default {
    components: { Loader, VMarkdown, ImportantMessage },
    data() {
        return {
            isLoading: true,
            contract: '',
            submitting: false,
            countries: [
                'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina', 'Armenia', 'Australia', 'Austria',
                'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
                'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia',
                'Cameroon', 'Canada', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica',
                'Croatia', 'Cuba', 'Cyprus', 'Czechia', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'East Timor', 'Ecuador',
                'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France',
                'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau',
                'Guyana', 'Haiti', 'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland',
                'Israel', 'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Korea, North', 'Korea, South',
                'Kosovo', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein',
                'Lithuania', 'Luxembourg', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania',
                'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar',
                'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Macedonia', 'Norway',
                'Oman', 'Pakistan', 'Palau', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal',
                'Qatar', 'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe',
                'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia',
                'South Africa', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria', 'Taiwan',
                'Tajikistan', 'Tanzania', 'Thailand', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu',
                'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela',
                'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
            ],
            formData: {
                name: '',
                address: '',
                country: '',
                index: '',
                city: '',
                state: '',
                bank_details: ''
            },
            devices: [],
            loadingDevices: false,
            withdrawals: [],
            loadingWithdrawals: false,
            isWithdrawalProcessing: false
        };
    },
    computed: {
        title() {
            return 'Auto Reimbursement';
        },
        metaInfo() {
            return {
                title: 'Auto Reimbursement',
                meta: [
                    {
                        name: 'description',
                        content: 'Auto Reimbursement information',
                    },
                    {
                        name: 'keywords',
                        content: 'auto, reimbursement',
                    },
                ],
            };
        },
        hasSignedAgreement() {
            return this.$store.getters.config?.plus_account?.is_automatic_reimbursement;
        },
        agreementDetails() {
            const plusAccount = this.$store.getters.config?.plus_account || {};
            return {
                name: plusAccount.automatic_reimbursement_name || '',
                address: plusAccount.automatic_reimbursement_address || '',
                country: plusAccount.automatic_reimbursement_country || '',
                index: plusAccount.automatic_reimbursement_index || '',
                city: plusAccount.automatic_reimbursement_city || '',
                state: plusAccount.automatic_reimbursement_state || '',
                bank_details: plusAccount.automatic_reimbursement_bank_details || '',
                signed_at: plusAccount.automatic_reimbursement_signed_at || 0
            };
        },
        deviceResetAt() {
            return this.$store.getters.config?.plus_account?.device_reset_at || null;
        },
        formatDeviceResetDate() {
            if (!this.deviceResetAt) return '';
            return new Date(this.deviceResetAt * 1000).toLocaleDateString();
        },
        formattedSignedDate() {
            if (!this.agreementDetails.signed_at) return '';
            const date = new Date(this.agreementDetails.signed_at * 1000);
            return date.toLocaleString();
        },
        deviceCount() {
            return this.$store.getters.config?.plus_account?.automatic_reimbursement_devices_amount || 0;
        },
        isDevicesView() {
            return this.$route.path.endsWith('/devices');
        },
        isMobile() {
            return window.innerWidth < 768;
        },
        financialDetails() {
            const plusAccount = this.$store.getters.config?.plus_account || {};
            return {
                balance: this.formatCurrency(plusAccount.automatic_reimbursement_balance_eur),
                availableForWithdrawal: this.formatCurrency(plusAccount.automatic_reimbursement_available_for_withdrawal_eur),
                withdrawalPending: this.formatCurrency(plusAccount.automatic_reimbursement_withdrawal_pending_eur),
                withdrawalCompleted: this.formatCurrency(plusAccount.automatic_reimbursement_withdrawal_completed_eur)
            };
        },
        availableSlots() {
            const plusAccount = this.$store.getters.config?.plus_account || {};
            const usedSlots = parseInt(plusAccount.used_slots) || 0;
            var to_return = 20 - usedSlots;
            if (to_return < 0) {
                to_return = 0;
            }
            return to_return;
        },
        isWithdrawalsView() {
            return this.$route.path.endsWith('/withdrawals');
        },
        isDeviceLinked() {
            return this.$store.getters.isDeviceLinked;
        }
    },
    watch: {
        isDevicesView(newVal) {
            if (newVal && this.devices.length === 0 && this.isDeviceLinked) {
                this.loadDevices();
            }
        },
        isWithdrawalsView(newVal) {
            if (newVal && this.withdrawals.length === 0 && this.isDeviceLinked) {
                this.loadWithdrawals();
            }
        }
    },
    created() {
        this.isLoading = true;

        this.$api.post('get_defaults', { 'for': 'auto_reimbursement_md' }, false)
            .then((response) => {
                if (response.success && response.data) {
                    this.contract = response.data.contract || '';
                } else {
                    console.error('Failed to fetch markdown content:', response);
                }
            })
            .catch((error) => {
                console.error('Error fetching markdown content:', error);
            })
            .finally(() => {
                this.isLoading = false;

                // Load appropriate data based on the current route and device linkage
                if (this.isDeviceLinked) {
                    if (this.isDevicesView) {
                        this.loadDevices();
                    } else if (this.isWithdrawalsView) {
                        this.loadWithdrawals();
                    }
                }
            });
    },
    methods: {
        formatCurrency(value) {
            const num = parseFloat(value) || 0;
            return num.toFixed(2);
        },
        submitForm() {
            if (this.submitting) return;

            this.submitting = true;

            // When handleErrors is true (the 3rd parameter), the API service will:
            // 1. Show error notifications automatically
            // 2. Throw an error when result.success is false
            // 3. Only return result.data on success (not the full response with success property)
            this.$api.post('edit_plus_dev_account_reimbursement_settings', {
                name: this.formData.name,
                address: this.formData.address,
                country: this.formData.country,
                index: this.formData.index,
                city: this.formData.city,
                state: this.formData.state,
                bank_details: this.formData.bank_details
            }, true) // Setting handleErrors to true
                .then(() => { // We get data directly, not a response object with success property
                    // Update device information using the correct store actions
                    // First get the configuration which includes plus_account details
                    return this.$store.dispatch('getConfig')
                        .then(() => {
                            // Then get updated devices information
                            return this.$store.dispatch('getDevices');
                        })
                        .then(() => {
                            Vue.notify({
                                type: 'success',
                                text: 'Agreement has been successfully signed'
                            });
                        });
                })
                .catch((error) => {
                    // The API service already showed an error notification
                    console.error('Error submitting form:', error);
                })
                .finally(() => {
                    this.submitting = false;
                });
        },
        loadDevices() {
            this.loadingDevices = true;

            this.$api.getP2PPurchases({ type: 'premium' })
                .then((response) => {
                    this.devices = response.data || [];
                })
                .catch((error) => {
                    console.error('Error loading devices:', error);
                    Vue.notify({
                        type: 'error',
                        text: 'Failed to load registered devices'
                    });
                })
                .finally(() => {
                    this.loadingDevices = false;
                });
        },
        loadWithdrawals() {
            this.loadingWithdrawals = true;

            this.$api.getWithdrawals()
                .then((response) => {
                    this.withdrawals = response.data || [];
                })
                .catch((error) => {
                    console.error('Error loading withdrawals:', error);
                    Vue.notify({
                        type: 'error',
                        text: 'Failed to load withdrawal history'
                    });
                })
                .finally(() => {
                    this.loadingWithdrawals = false;
                });
        },
        requestWithdrawal() {
            if (this.isWithdrawalProcessing) return;

            this.isWithdrawalProcessing = true;

            const amount = this.financialDetails.availableForWithdrawal;
            const confirmMessage = `Are you sure to withdraw €${amount} to your configured payment method?`;

            this.$confirm(confirmMessage, { yesOrNo: true })
                .then(() => {
                    return this.$api.post('request_withdrawal', {}, true);
                })
                .then(() => {
                    // Refresh configuration data to update financial details
                    return this.$store.dispatch('getConfig');
                })
                .then(() => {
                    // Update device information
                    return this.$store.dispatch('getDevices');
                })
                .then(() => {
                    // On success and after refreshing data, redirect to withdrawals page
                    this.$router.push('/my/auto-reimbursement/withdrawals');
                })
                .catch((error) => {
                    // If it's a real error (not just cancellation of dialog), log it
                    if (error) {
                        console.error('Withdrawal request failed:', error);
                    }
                })
                .finally(() => {
                    this.isWithdrawalProcessing = false;
                });
        }
    }
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

.main {
    display: block;
}

.loader-container {
    display: flex;
    justify-content: center;
    padding: 50px 0;
}

.section {
    margin-bottom: 30px;

    :deep(img) {
        max-height: 140px;
        display: block;
        margin-left: auto;
        margin-right: auto;
    }
}

.form-section {
    max-width: 800px;
    margin: 0 auto 30px;
    padding: 20px;
    background-color: var(--background-secondary, #f5f5f5);
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border: 1px solid var(--border-color, #ddd);
}

.form-title {
    margin-bottom: 25px;
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary, #333);
    padding-bottom: 10px;
    border-bottom: 1px solid var(--border-color, #ddd);
}

.form-group {
    margin-bottom: 20px;
    width: 100%;
}

.form-row {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 16px;

    .form-group {
        flex: 1;
        min-width: 200px;
        margin-bottom: 0;
    }
}

label {
    display: block;
    margin-bottom: 8px;
    font-weight: 600;
    color: var(--text-primary, #333);
}

.bank-info {
    margin-bottom: 8px;
    font-size: 14px;
    color: var(--text-secondary, #666);
    background-color: var(--background-tertiary, #f0f3f7);
    padding: 8px 12px;
    border-radius: 4px;
    border-left: 3px solid var(--accent-color, #2c87f0);
}

.form-control {
    width: 100%;
    padding: 12px;
    border: 1px solid var(--border-color, #ccc);
    border-radius: 4px;
    background-color: var(--input-background, #fff);
    color: var(--text-primary, #333);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    font-size: 14px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:focus {
        outline: none;
        border-color: var(--accent-color, #2c87f0);
        box-shadow: 0 0 0 2px rgba(44, 135, 240, 0.25);
    }

    &:hover:not(:focus) {
        border-color: var(--border-color-hover, #aaa);
    }
}

select.form-control {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23555' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    background-size: 16px;
    padding-right: 40px;
    cursor: pointer;
}

textarea.form-control {
    min-height: 100px;
    resize: vertical;
}

.btn-primary {
    display: inline-block;
    padding: 12px 24px;
    background-color: var(--accent-color, #2c87f0);
    color: white;
    border: none;
    border-radius: 4px;
    font-weight: 500;
    font-size: 15px;
    cursor: pointer;
    transition: background-color 0.2s, transform 0.1s;

    &:hover {
        background-color: var(--accent-color-hover, #1a75e0);
    }

    &:active {
        transform: translateY(1px);
    }
}

.form-actions {
    margin-top: 24px;
    text-align: right;
}

.agreement-details {
    max-width: 800px;
    margin: 0 auto 30px;
    padding: 20px;
    background-color: var(--background-tertiary);
    border-radius: 8px;
}

.usage-section {
    margin-bottom: 20px;
}

.section-subtitle {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 16px;
    color: var(--text-primary, #333);
}

.usage-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
}

.usage-card {
    flex: 1;
    min-width: 200px;
    padding: 15px;
    background-color: var(--bg);
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border: 1px solid var(--tertiary-accent);
}

.usage-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.usage-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
}

.usage-value {
    font-size: 24px;
    font-weight: 700;
    color: var(--text-primary);
    margin: 8px 0;
}

.usage-label {
    font-size: 14px;
    color: var(--text-secondary);
}

.details-link {
    color: var(--accent-color, #2c87f0);
    text-decoration: none;
    font-size: 14px;

    &:hover {
        text-decoration: underline;
    }
}

.agreement-section {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid var(--border-color, #ddd);
}

.details-container {
    margin-top: 16px;
}

.detail-row {
    display: flex;
    padding: 8px 0;
    border-bottom: 1px solid var(--border-color);

    &:last-child {
        border-bottom: none;
    }
}

.detail-label {
    width: 150px;
    font-weight: 500;
    color: var(--text-secondary);
}

.detail-value {
    flex: 1;
    word-break: break-word;
}

/* Devices Detail View Styles */
.back-section {
    margin-bottom: 20px;
}

.back-link {
    color: var(--accent-color, #2c87f0);
    text-decoration: none;
    font-weight: 500;

    &:hover {
        text-decoration: underline;
    }
}

.section-title {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 20px;
    color: var(--text-primary, #333);
}

.table-devices-wrapper {
    overflow-x: auto;
    margin-bottom: 20px;
    width: 100%;
}

.table-devices {
    width: 100%;
    min-width: 100%;
    table-layout: fixed;
    border-collapse: collapse;

    &__head {
        background-color: var(--tertiary-bg);
    }

    &__row {
        border-bottom: 1px solid var(--border-color, #ddd);

        &:hover {
            background-color: var(--background-hover, #f9f9f9);
        }
    }

    &__cell {
        padding: 12px 16px;
        text-align: left;
        word-wrap: break-word;
        overflow-wrap: break-word;
        word-break: break-all;
        color: var(--text-primary);

        &:first-child {
            font-family: monospace;
            font-size: 12px;
        }
    }
}

.devices {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.device {
    display: grid;
    grid-template-columns: 120px 1fr;
    background-color: var(--tertiary-bg);
    padding: 15px;
    border-radius: 6px;
    border: 1px solid var(--tertiary-accent);

    &__heading {
        font-weight: 600;
        color: var(--text-secondary);
    }

    span {
        word-wrap: break-word;
        overflow-wrap: break-word;
        word-break: break-all;
        color: var(--text-primary);
    }
}

/* Withdrawals Detail View Styles */
.withdrawals-details {
    width: 100%;
    max-width: none;
    margin: 0 0 30px;
    padding: 20px;
    background-color: var(--bg);
    border-radius: 8px;
}

.table-withdrawals-wrapper {
    overflow-x: auto;
    margin-bottom: 20px;
    width: 100%;
}

.table-withdrawals {
    width: 100%;
    min-width: 100%;
    table-layout: fixed;
    border-collapse: collapse;

    &__head {
        background-color: var(--tertiary-bg);
    }

    &__row {
        border-bottom: 1px solid var(--tertiary-accent);

        &:hover {
            background-color: var(--lighter-secondary);
        }
    }

    &__cell {
        padding: 12px 16px;
        text-align: left;
        word-wrap: break-word;
        overflow-wrap: break-word;
        word-break: break-all;
        color: var(--text-primary);

        &:first-child {
            font-family: monospace;
        }
    }

    &__status-text {
        font-size: 13px;
        color: var(--text-tertiary);
        margin-top: 4px;
        font-style: italic;
    }
}

.withdrawals {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.withdrawal {
    display: grid;
    grid-template-columns: 120px 1fr;
    background-color: var(--bg);
    padding: 15px;
    border-radius: 6px;
    border: 1px solid var(--tertiary-accent);

    &__heading {
        font-weight: 600;
        color: var(--text-secondary);
    }

    span {
        color: var(--text-primary);
    }

    &__status-text {
        font-size: 13px;
        color: var(--text-tertiary);
        margin-top: 4px;
        font-style: italic;
    }
}

.no-items-notice {
    text-align: center;
    padding: 20px;
    background-color: var(--bg);
    color: var(--text-primary);
    border-radius: 8px;
    margin-bottom: 20px;
    border: 1px solid var(--tertiary-accent);
}

.btn-withdrawal {
    display: block;
    width: 100%;
    padding: 8px;
    margin-top: 12px;
    background-color: var(--accent-color, #2c87f0);
    color: white;
    border: none;
    border-radius: 4px;
    font-weight: 500;
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.2s, transform 0.1s;

    &:hover {
        background-color: var(--accent-color-hover, #1a75e0);
    }

    &:active {
        transform: translateY(1px);
    }

    &:disabled {
        background-color: var(--tertiary-accent, #ccc);
        color: var(--text-tertiary, #888);
        cursor: not-allowed;
        transform: none;
        opacity: 0.7;
    }
}

.invoice-link {
    color: var(--accent-color, #2c87f0);
    text-decoration: none;
    font-size: 14px;

    &:hover {
        text-decoration: underline;
    }
}

.status-indicator {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-weight: 500;
    font-size: 13px;
    text-align: center;

    &--positive {
        background-color: rgba(40, 167, 69, 0.2);
        color: var(--text-success, #28a745);
    }

    &--negative {
        background-color: rgba(220, 53, 69, 0.2);
        color: var(--text-error, #dc3545);
    }
}

.device-linking {
    max-width: 800px;
    margin: 0 auto 30px;
    padding: 30px;
    text-align: center;
    background-color: var(--bg);
    border-radius: 8px;
    border: 1px solid var(--tertiary-accent);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    .form-title {
        margin-bottom: 25px;
        font-size: 18px;
        font-weight: 600;
        color: var(--text-primary);
    }

    .btn-primary {
        display: inline-block;
        padding: 12px 24px;
        background-color: var(--accent-color, #2c87f0);
        color: white;
        text-decoration: none;
        border: none;
        border-radius: 4px;
        font-weight: 500;
        font-size: 15px;
        cursor: pointer;
        transition: background-color 0.2s, transform 0.1s;

        &:hover {
            background-color: var(--accent-color-hover, #1a75e0);
        }

        &:active {
            transform: translateY(1px);
        }
    }
}

.reimbursement-info {
    max-width: 800px;
    margin: 0 auto 30px;
    padding: 15px 20px;
    background-color: var(--tertiary-bg);
    border-radius: 8px;
    border-left: 4px solid var(--accent-color, #2c87f0);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

    p {
        margin: 0;
        line-height: 1.5;
        color: var(--text-primary);
        font-size: 15px;
    }
}

.device-reset-message {
    margin-top: 16px;
    margin-bottom: 0;
}
</style>